import React from 'react';
import { Layers, Bot, Share2, Wrench, Check as CheckIcon, X as XIcon, Minus } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, RuleLabel,
  TextLink, BigStat, Marquee, ClosingCta,
} from '../components/V2Shared';
import { WHY_PILLARS, SPRAWL_STACK, MANUAL_TASKS } from '../data/site';

const PILLAR_ICONS = [Layers, Bot, Share2, Wrench];

/* capability matrix: asha vs the categories practices usually assemble */
const MATRIX_ROWS = [
  { cap: 'Practice management & charting', asha: true, ai: false, comms: false, pms: true },
  { cap: 'Phones, texting, fax, forms', asha: true, ai: 'partial', comms: true, pms: false },
  { cap: 'Insurance verification — procedure level', asha: true, ai: false, comms: false, pms: 'partial' },
  { cap: 'Claims, scrubbing, and submission', asha: true, ai: false, comms: false, pms: 'partial' },
  { cap: 'EOB posting & reconciliation', asha: true, ai: false, comms: false, pms: false },
  { cap: 'Denial analysis & appeals', asha: true, ai: false, comms: false, pms: false },
  { cap: 'AI that completes workflows end to end', asha: true, ai: 'partial', comms: false, pms: false },
  { cap: 'Agents with shared patient memory', asha: true, ai: false, comms: false, pms: false },
  { cap: 'Clinical AI inside the chart', asha: true, ai: false, comms: false, pms: 'partial' },
  { cap: 'One login, one data model', asha: true, ai: false, comms: false, pms: false },
];

const Cell = ({ v }) => (
  <div className="flex justify-center">
    {v === true ? (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#eef3ff]">
        <CheckIcon className="h-3.5 w-3.5 text-[#1a46e5]" strokeWidth={3} />
      </span>
    ) : v === 'partial' ? (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#f6f9ff]">
        <Minus className="h-3.5 w-3.5 text-[#7c89a8]" strokeWidth={3} />
      </span>
    ) : (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#faf7f6]">
        <XIcon className="h-3.5 w-3.5 text-[#d8b4ac]" strokeWidth={3} />
      </span>
    )}
  </div>
);

const V2WhyAsha = () => (
  <V2Layout title="Why Asha — The Case for an Operating System">
    <PageHero
      eyebrow="Why Asha / the argument"
      title={<>Point solutions can't get you there. <span className="text-[#1a46e5]">Here's why.</span></>}
      body="Every tool in your stack automates a slice of a workflow, then hands the rest back to your team. The slice gets faster; the workflow stays manual. Real automation needs the whole thing — the phones, the chart, the claim, the ledger — in one system an AI can actually operate."
    >
      <CtaGhost to="/book-demo">Make us prove it</CtaGhost>
      <TextLink to="/v2/platform">See the platform</TextLink>
    </PageHero>

    {/* the threshold argument */}
    <section className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="01 / the automation threshold" />
        <div className="mt-12 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <SectionHead
            eyebrow="Why half-automation fails"
            title={<>You can't half-automate a claims cycle.</>}
            body="A verification tool that can't see the schedule still needs a human to know who's coming. An AI receptionist that can't write to the PMS still needs a human to book. Every seam between tools is a place where automation stops and your staff starts. Asha owns every layer — so the workflow runs from first ring to posted payment without a hand-back."
          />
          <FadeUp delay={0.1}>
            <div className="space-y-3">
              {[
                { label: 'point solution', text: 'Tool verifies eligibility → human reads the PDF → human updates the chart → human tells the front desk', broken: true },
                { label: 'point solution', text: 'AI answers the call → human checks the schedule → human books → human sends the forms', broken: true },
                { label: 'asha', text: 'Amy answers → checks live coverage with Amanda → books against real scheduling rules → Morgan sends forms → chart updated. Zero hand-backs.', broken: false },
              ].map((r, i) => (
                <div
                  key={i}
                  className={`rounded-2xl border p-6 ${
                    r.broken ? 'border-[#eee2df] bg-[#fdfbfa]' : 'border-[#1a46e5]/30 bg-[#eef3ff]'
                  }`}
                >
                  <div className={`v2-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] ${r.broken ? 'text-[#c4988c]' : 'text-[#1a46e5]'}`}>
                    {r.label} {r.broken ? '— automation breaks at every arrow' : '— one continuous workflow'}
                  </div>
                  <p className="mt-2.5 text-[14.5px] leading-relaxed text-[#3f4d6e]">{r.text}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* pillars */}
    <section className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="02 / four reasons it has to be an os" />
        <Stagger className="mt-12 grid gap-5 md:grid-cols-2">
          {WHY_PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <StaggerItem key={p.title} className="h-full">
                <div className="v2-card h-full rounded-2xl p-8">
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eef3ff]">
                      <Icon className="h-5 w-5 text-[#1a46e5]" />
                    </span>
                    <span className="v2-display text-[28px] font-semibold text-[#dde5f3]">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="v2-display mt-5 text-[23px] font-semibold leading-tight text-[#0a1633]">{p.title}</div>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#3f4d6e]">{p.body}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>

    {/* capability matrix */}
    <section className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="03 / the honest comparison" />
        <div className="mt-12">
          <SectionHead
            eyebrow="Asha vs. the assembled stack"
            title={<>What each category actually covers.</>}
            body="Not a swipe at any vendor — point solutions are good at their slice. The problem is the slice."
          />
        </div>
        <FadeUp delay={0.1}>
          <div className="mt-12 overflow-x-auto">
            <div className="min-w-[820px] overflow-hidden rounded-2xl border border-[#dde5f3]">
              <div className="grid grid-cols-[1.6fr_repeat(4,1fr)] bg-[#f6f9ff]">
                <div className="v2-mono px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7c89a8]">capability</div>
                {['Asha OS', 'AI receptionist tools', 'Comms platforms', 'Legacy PMS'].map((h, i) => (
                  <div key={h} className={`px-3 py-4 text-center text-[13px] font-bold ${i === 0 ? 'bg-[#1a46e5] text-white' : 'text-[#0a1633]'}`}>
                    {h}
                  </div>
                ))}
              </div>
              {MATRIX_ROWS.map((r) => (
                <div key={r.cap} className="v2-row grid grid-cols-[1.6fr_repeat(4,1fr)] items-center">
                  <div className="px-6 py-3.5 text-[14px] font-semibold text-[#0a1633]">{r.cap}</div>
                  <div className="bg-[#eef3ff]/60 py-3.5"><Cell v={r.asha} /></div>
                  <div className="py-3.5"><Cell v={r.ai} /></div>
                  <div className="py-3.5"><Cell v={r.comms} /></div>
                  <div className="py-3.5"><Cell v={r.pms} /></div>
                </div>
              ))}
            </div>
          </div>
          <p className="v2-mono mt-4 text-right text-[10.5px] uppercase tracking-[0.14em] text-[#a0aac4]">
            ✓ full coverage · – partial / varies by vendor · ✕ not offered
          </p>
        </FadeUp>
      </div>
    </section>

    {/* the stack it replaces */}
    <section className="px-6 py-16 md:py-20">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="04 / what consolidation looks like" />
        <div className="mt-12 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <SectionHead
            eyebrow="Ten categories, one product"
            title={<>Retire the stack. Keep the capability.</>}
            body="Everything on this list exists inside Asha as a native module — same login, same data model, same patient timeline. The capability stays; the sprawl, the sync issues, and the vendor roulette go."
          />
          <FadeUp delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-[#dde5f3]">
              {SPRAWL_STACK.map((s, i) => (
                <div key={s.name} className={`v2-row flex items-baseline gap-5 px-6 py-3 ${i === 0 ? '!border-t-0' : ''}`}>
                  <span className="v2-mono w-7 shrink-0 text-[11px] text-[#a8bcf0]">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-[14.5px] font-semibold text-[#0a1633]">{s.name}</span>
                  <span className="v2-mono ml-auto text-right text-[11px] text-[#a0aac4] line-through decoration-[#d8614f]/60">{s.vendors}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* 40 tasks marquee */}
    <section className="py-12">
      <Marquee items={MANUAL_TASKS.slice(0, 20)} />
      <Marquee items={MANUAL_TASKS.slice(20)} fast />
    </section>

    {/* credibility panel */}
    <section className="px-6 py-16 md:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <SectionHead
            dark
            center
            eyebrow="built for this"
            title={<>An OS is a bigger promise. We built like it.</>}
            body="Asha is backed by Y Combinator and General Catalyst, and the AI is built in-house — inside the platform, on dental workflows — by a team that came to dentistry from frontier AI. We're not reselling a wrapper; we're building the operating system."
          />
          <FadeUp delay={0.15}>
            <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="10+" label="software products" />
              <BigStat dark value="20+" label="ai employees" />
              <BigStat dark value="40+" label="tasks automated" />
              <BigStat dark value="1" label="operating system" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <ClosingCta
      title="Skeptical? Good. Bring your hardest workflow."
      body="A stale verification, a denied SRP, a 9 pm emergency call — pick the scenario and we'll run it live in the demo."
    />
  </V2Layout>
);

export default V2WhyAsha;
